import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, TestTube, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const ProxyManager = () => {
  const [proxies, setProxies] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    proxy_url: '',
    proxy_type: 'http',
    username: '',
    password: '',
    exchange_name: '',
    priority: 0
  });
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProxies();
    fetchSettings();
  }, []);

  const fetchProxies = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/proxies`, { withCredentials: true });
      setProxies(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки прокси');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/proxy-settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleAddProxy = async () => {
    try {
      await axios.post(`${backendUrl}/api/admin/proxies`, formData, { withCredentials: true });
      toast.success('Прокси добавлен');
      setDialogOpen(false);
      fetchProxies();
      setFormData({ name: '', proxy_url: '', proxy_type: 'http', username: '', password: '', exchange_name: '', priority: 0 });
    } catch (error) {
      toast.error('Ошибка добавления прокси');
    }
  };

  const handleDeleteProxy = async (proxyId) => {
    if (!window.confirm('Удалить этот прокси?')) return;
    try {
      await axios.delete(`${backendUrl}/api/admin/proxies/${proxyId}`, { withCredentials: true });
      toast.success('Прокси удалён');
      fetchProxies();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const handleTestProxy = async (proxyId) => {
    const toastId = toast.loading('Проверка прокси...');
    try {
      const response = await axios.post(`${backendUrl}/api/admin/proxies/${proxyId}/test`, {}, { withCredentials: true });
      const result = response.data;
      
      toast.dismiss(toastId);
      
      if (result.overall_status === 'healthy') {
        toast.success(`✅ Прокси работает отлично! (${result.success_rate})`);
      } else if (result.overall_status === 'partial') {
        toast.warning(`⚠️ Прокси работает частично (${result.success_rate})`);
      } else {
        toast.error(`❌ Прокси не работает`);
      }
      
      // Show detailed results
      if (result.tests && result.tests.length > 0) {
        const details = result.tests.map(t => 
          `${t.name}: ${t.status === 'success' ? '✓' : '✗'} ${t.error || t.message || ''}`
        ).join('\n');
        
        // Show modal with details
        if (window.confirm(`Результаты проверки:\n\n${details}\n\nIP: ${result.tests[0]?.ip || 'N/A'}\n\nОбновить статистику?`)) {
          fetchProxies();
        }
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error('Ошибка тестирования');
    }
  };
  
  const handleTestAllProxies = async () => {
    const toastId = toast.loading('Проверка всех прокси...');
    try {
      const response = await axios.post(`${backendUrl}/api/admin/proxies/test-all`, {}, { withCredentials: true });
      toast.dismiss(toastId);
      
      const results = response.data.results;
      const healthy = results.filter(r => r.overall_status === 'healthy').length;
      const partial = results.filter(r => r.overall_status === 'partial').length;
      const failed = results.filter(r => r.overall_status === 'failed' || r.overall_status === 'error').length;
      
      toast.success(`Проверка завершена:\n✅ Работают: ${healthy}\n⚠️ Частично: ${partial}\n❌ Не работают: ${failed}`);
      
      fetchProxies();
    } catch (error) {
      toast.dismiss(toastId);
      toast.error('Ошибка массовой проверки');
    }
  };

  const handleUpdateSettings = async (newSettings) => {
    try {
      await axios.post(`${backendUrl}/api/admin/proxy-settings`, newSettings, { withCredentials: true });
      toast.success('Настройки обновлены');
      fetchSettings();
    } catch (error) {
      toast.error('Ошибка обновления настроек');
    }
  };

  const handleToggleProxy = async (proxyId, isActive) => {
    try {
      await axios.put(`${backendUrl}/api/admin/proxies/${proxyId}`, { is_active: !isActive }, { withCredentials: true });
      fetchProxies();
    } catch (error) {
      toast.error('Ошибка обновления');
    }
  };

  return (
    <div className="space-y-6">
      {/* Settings Card */}
      <Card className="bg-[#1a1a1a] border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Глобальные настройки прокси</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-white">Использовать прокси</Label>
              <p className="text-sm text-gray-400">Включить прокси для всех запросов к биржам</p>
            </div>
            <Switch
              checked={settings.proxy_enabled || false}
              onCheckedChange={(checked) => handleUpdateSettings({ ...settings, proxy_enabled: checked })}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-white">Автоматическая ротация при ошибках</Label>
              <p className="text-sm text-gray-400">Переключаться на другой прокси при сбоях</p>
            </div>
            <Switch
              checked={settings.auto_rotate_on_error || false}
              onCheckedChange={(checked) => handleUpdateSettings({ ...settings, auto_rotate_on_error: checked })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Proxies List */}
      <Card className="bg-[#1a1a1a] border-gray-800">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-white">Прокси-серверы</CardTitle>
            <div className="flex gap-2">
              <Button 
                onClick={handleTestAllProxies} 
                variant="outline" 
                className="border-blue-800 text-blue-400 hover:bg-blue-900/20"
              >
                <TestTube className="h-4 w-4 mr-2" />
                Проверить все
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#00ff88] text-black hover:opacity-90">
                    <Plus className="h-4 w-4 mr-2" />
                    Добавить прокси
                  </Button>
                </DialogTrigger>
              <DialogContent className="bg-[#1a1a1a] border-gray-800 text-white">
                <DialogHeader>
                  <DialogTitle className="text-white">Добавить прокси-сервер</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Название</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-[#0a0a0a] border-gray-800 text-white" />
                  </div>
                  <div>
                    <Label>Тип прокси</Label>
                    <select value={formData.proxy_type} onChange={(e) => setFormData({ ...formData, proxy_type: e.target.value })} className="w-full bg-[#0a0a0a] border border-gray-800 rounded-md px-3 py-2 text-white">
                      <option value="http">HTTP</option>
                      <option value="https">HTTPS</option>
                      <option value="socks5">SOCKS5</option>
                    </select>
                  </div>
                  <div>
                    <Label>URL прокси (host:port)</Label>
                    <Input value={formData.proxy_url} onChange={(e) => setFormData({ ...formData, proxy_url: e.target.value })} placeholder="1.2.3.4:8080" className="bg-[#0a0a0a] border-gray-800 text-white" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Username (опц.)</Label>
                      <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className="bg-[#0a0a0a] border-gray-800 text-white" />
                    </div>
                    <div>
                      <Label>Password (опц.)</Label>
                      <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="bg-[#0a0a0a] border-gray-800 text-white" />
                    </div>
                  </div>
                  <div>
                    <Label>Биржа (опц.)</Label>
                    <select value={formData.exchange_name} onChange={(e) => setFormData({ ...formData, exchange_name: e.target.value })} className="w-full bg-[#0a0a0a] border border-gray-800 rounded-md px-3 py-2 text-white">
                      <option value="">Все биржи</option>
                      <option value="binance">Binance</option>
                      <option value="bybit">ByBit</option>
                      <option value="okx">OKX</option>
                    </select>
                  </div>
                  <div>
                    <Label>Приоритет</Label>
                    <Input type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })} className="bg-[#0a0a0a] border-gray-800 text-white" />
                  </div>
                  <Button onClick={handleAddProxy} className="w-full bg-[#00ff88] text-black hover:opacity-90">Добавить</Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-400">Загрузка...</p>
          ) : proxies.length === 0 ? (
            <p className="text-gray-400">Нет прокси-серверов</p>
          ) : (
            <div className="space-y-3">
              {proxies.map((proxy) => (
                <div key={proxy.id} className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-white font-semibold">{proxy.name}</h3>
                        <span className="text-xs px-2 py-1 rounded bg-blue-900/30 text-blue-400">{proxy.proxy_type.toUpperCase()}</span>
                        {proxy.exchange_name && (
                          <span className="text-xs px-2 py-1 rounded bg-purple-900/30 text-purple-400">{proxy.exchange_name}</span>
                        )}
                        {proxy.is_active ? (
                          <span className="text-xs px-2 py-1 rounded bg-green-900/30 text-green-400 flex items-center gap-1">
                            <Check className="h-3 w-3" />Активен
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-red-900/30 text-red-400 flex items-center gap-1">
                            <X className="h-3 w-3" />Неактивен
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">{proxy.proxy_url}</p>
                      <div className="flex gap-4 mt-2 text-xs text-gray-500">
                        <span>Приоритет: {proxy.priority}</span>
                        <span className={proxy.success_count > 0 ? 'text-green-400' : ''}>Успехов: {proxy.success_count}</span>
                        <span className={proxy.fail_count > 0 ? 'text-red-400' : ''}>Ошибок: {proxy.fail_count}</span>
                        {proxy.success_count + proxy.fail_count > 0 && (
                          <span className="text-blue-400">
                            Success Rate: {Math.round((proxy.success_count / (proxy.success_count + proxy.fail_count)) * 100)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={proxy.is_active} onCheckedChange={() => handleToggleProxy(proxy.id, proxy.is_active)} />
                      <Button size="sm" variant="outline" onClick={() => handleTestProxy(proxy.id)} className="border-gray-700 text-gray-300 hover:bg-gray-800">
                        <TestTube className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteProxy(proxy.id)} className="border-red-900 text-red-400 hover:bg-red-900/20">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProxyManager;